import { type FormEvent, useState } from "react";
import { useRole } from "../../components/AppShell";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useCompany, useUpdateCompany } from "../../lib/queries";

const FIELDS: { key: Key; label: string; variable?: string; placeholder: string; max: number; type?: string }[] = [
  { key: "phone", label: "Teléfono", variable: "telefono_empresa", placeholder: "Ej.: 2440 1234", max: 40 },
  { key: "email", label: "Correo", placeholder: "ventas@empresa.com", max: 200, type: "email" },
  {
    key: "address",
    label: "Dirección",
    variable: "direccion",
    placeholder: "Ej.: 200 m oeste del Parque Central, Alajuela",
    max: 300,
  },
  { key: "website", label: "Sitio web", variable: "sitio_web", placeholder: "Ej.: empresa.cr", max: 200 },
];

type Key = "phone" | "email" | "address" | "website";

/** The company's own contact details: Lumi gives them and template variables use them. */
export function CompanyCard() {
  const role = useRole();
  const canEdit = role === "owner" || role === "admin";
  const company = useCompany();
  const update = useUpdateCompany();
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<Record<Key, string>>>({});
  const value = (k: Key) => draft[k] ?? company.data?.[k] ?? "";
  const dirty = FIELDS.some((f) => draft[f.key] !== undefined && draft[f.key] !== (company.data?.[f.key] ?? ""));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync(Object.fromEntries(FIELDS.map((f) => [f.key, value(f.key)])));
      setDraft({});
      toast("Datos de la empresa guardados");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudieron guardar.");
    }
  }

  return (
    <div className="card">
      <form className="card-body" onSubmit={onSubmit}>
        <div>
          <h3>Contacto</h3>
          <p className="muted">
            Lumi los da a los clientes, y las plantillas los usan con variables como{" "}
            <span className="mono">{"{{direccion}}"}</span>.
          </p>
        </div>
        <div className="company-fields">
          {FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label htmlFor={`company-${f.key}`}>
                {f.label} {f.variable && <span className="faint mono">{`{{${f.variable}}}`}</span>}
              </label>
              <input
                className="input"
                id={`company-${f.key}`}
                type={f.type ?? "text"}
                value={value(f.key)}
                placeholder={f.placeholder}
                maxLength={f.max}
                disabled={!canEdit}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              />
            </div>
          ))}
        </div>
        {canEdit && (
          <div>
            <button className="btn btn-primary" type="submit" disabled={!dirty || update.isPending}>
              {update.isPending ? "Guardando…" : "Guardar"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
